/**
 * Safely extract an array from an API response payload.
 * Handles direct arrays and wrapped responses like:
 *   [...], { data: [...] }, { results: [...] },
 *   or nested { data: { results: [...] } }.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function extractArray<T = any>(payload: unknown): T[] {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object') {
        const obj = payload as Record<string, unknown>;
        if (Array.isArray(obj.data)) return obj.data;
        if (Array.isArray(obj.results)) return obj.results;
        // Handle nested { data: { results: [...] } }
        if (obj.data && typeof obj.data === 'object' && !Array.isArray(obj.data)) {
            const inner = obj.data as Record<string, unknown>;
            if (Array.isArray(inner.results)) return inner.results;
            if (Array.isArray(inner.data)) return inner.data;
        }
    }
    return [];
}

/**
 * Facebook-style relative time: "Just now", "5m ago", "3h ago", "2d ago".
 * Falls back to an absolute date once the timestamp is older than a week.
 */
export function formatTimeAgo(value: string | Date): string {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return typeof value === 'string' ? value : '';
    }
    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diffSec < 60) return 'Just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

/**
 * Format a monetary amount with grouped digits (en-IN). Whole numbers show
 * with no decimals (e.g. "4500.0000" -> "4,500"); fractional amounts show up
 * to 2 decimal places (e.g. "4500.5" -> "4,500.5", "4500.256" -> "4,500.26").
 * Returns "0" for empty/nullish input.
 */
export function formatMoney(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') return '0';
    return Number(value).toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}

/**
 * Format a stock/quantity amount (backend sends these as 4-decimal strings,
 * e.g. "10.0000"). Trims trailing zeros while keeping whole numbers clean:
 * "10.0000" -> "10", "3.5000" -> "3.5". Returns "0" for empty/nullish input.
 */
export function formatQuantity(value: string | number | null | undefined): string {
    if (value === null || value === undefined || value === '') return '0';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

const UNPREFIXED_KEYS = new Set(['non_field_errors', 'detail', 'message', '__all__']);

function humanizeFieldName(key: string): string {
    const spaced = key.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
    if (!spaced) return '';
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Recursively collect leaf error messages with their humanized field path so
 * the caller can render "Group field is required" instead of an anonymous
 * "This field is required" five times in a row.
 */
type LabeledMessage = { label: string; message: string };

function collectLabeledMessages(value: unknown, label: string): LabeledMessage[] {
    if (typeof value === 'string') return [{ label, message: value }];
    if (Array.isArray(value)) return value.flatMap((v) => collectLabeledMessages(v, label));
    if (value && typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        // A `reason` field is a backend convention for an already-composed,
        // human-readable sentence (e.g. return/stock validation errors) — use
        // it as-is instead of flattening every sibling field (product_slug,
        // required, available, ...) into a redundant wall of text.
        if (typeof obj.reason === 'string') {
            return [{ label: '', message: obj.reason }];
        }
        return Object.entries(obj).flatMap(([key, v]) => {
            const nextLabel = UNPREFIXED_KEYS.has(key) ? label : humanizeFieldName(key);
            return collectLabeledMessages(v, nextLabel);
        });
    }
    return [];
}

function formatLabeled({ label, message }: { label: string; message: string }): string {
    if (!label) return message;
    const trimmed = message.trim();
    if (!trimmed) return `${label} field is required.`;
    // If the message already names the field, don't double up.
    if (trimmed.toLowerCase().startsWith(label.toLowerCase())) return trimmed;
    if (/this field is required/i.test(trimmed)) return `${label} field is required.`;
    return `${label}: ${trimmed}`;
}

/**
 * Extract a readable error message from API validation errors.
 * Handles shapes like:
 *   - { name: ["error msg"], symbol: ["error msg"] }
 *   - { vendor: { pan_number: ["error msg"] } }
 *   - { stock: [{ product_slug, product_name, required, available, reason }] }
 *     (any object carrying a string `reason` — its sibling fields are
 *     machine-readable context, not meant for display, so `reason` alone is used)
 *   - { message: "error msg" }
 *   - { detail: "error msg" }
 *   - "plain string"
 */
export function extractApiError(
    data: unknown,
    fallback: string,
): string {
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    if (typeof data !== 'object') return fallback;

    const obj = data as Record<string, unknown>;

    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.detail === 'string') return obj.detail;

    const labeled = collectLabeledMessages(obj, '');
    if (labeled.length === 0) return fallback;
    return labeled.map(formatLabeled).join(' ');
}

const PERMISSION_FRIENDLY: Record<string, string> = {
    'announcement.create_announcement':
        "You don't have permission to create announcements. Please ask your administrator to grant you the 'Create Announcement' permission.",
    'announcement.edit_announcement':
        "You don't have permission to edit announcements. Please ask your administrator to grant you the 'Edit Announcement' permission.",
    'announcement.change_announcement':
        "You don't have permission to edit announcements. Please ask your administrator to grant you the 'Edit Announcement' permission.",
    'order.request_return':
        "You don't have permission to request returns. Please ask your administrator to grant you the 'Request Return' permission.",
    'order.approve_return':
        "You don't have permission to approve or reject returns. Please ask your administrator to grant you the 'Approve Return' permission.",
    'whatsapp.view_conversation':
        "You don't have permission to view WhatsApp conversations. Please ask your administrator to grant you the 'View WhatsApp Conversations' permission.",
    'whatsapp.send_message':
        "You don't have permission to send WhatsApp replies. Please ask your administrator to grant you the 'Send WhatsApp Message' permission.",
    'whatsapp.manage_account':
        "You don't have permission to manage the WhatsApp account. Please ask your administrator to grant you the 'Manage WhatsApp Account' permission.",
};

/**
 * If `message` looks like a raw "Permission denied: <code>" string from the
 * API, return a friendly user-facing version. Otherwise return the input
 * unchanged.
 */
export function humanizePermissionError(message: string): string {
    const match = message.match(/^\s*Permission denied:\s*([\w.]+)\s*$/i);
    if (!match) return message;
    const code = match[1];
    return PERMISSION_FRIENDLY[code]
        ?? "You don't have permission to perform this action. Please contact your administrator.";
}
